"use client";

/**
 * SceneContainer — landing-specific wrapper for each landing scene.
 *
 * Role: the "film chapter" shell. Gives every scene:
 *   - stable section id for anchor scroll and scroll-progress mapping
 *   - `whileInView` fade-up entrance (400ms, Apple ease-out)
 *   - scroll-margin-top offset so anchor-jumps clear the sticky
 *     scroll-progress bar
 *   - ARIA region landmark with aria-labelledby for screen readers
 *
 * Reduced-motion: useReducedMotion() drops the entrance animation to an
 * instant render. Scene content still renders; only the wrapper motion is
 * disabled.
 *
 * Keep this wrapper thin: scene-specific motion (streaming H1, waterfall,
 * word-stagger) lives inside each scene component, not here.
 */

import { motion, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";

interface SceneContainerProps {
  id: string;
  /** Optional aria-label for screen readers — falls back to aria-labelledby on a heading with id=`${id}-title`. */
  ariaLabel?: string;
  /** Override the default `min-h-screen` (scenes 1,4 are taller; scene 2 waterfall is ~200vh). */
  minHeight?: string;
  className?: string;
  children: ReactNode;
}

const APPLE_EASE_OUT = [0.16, 1, 0.3, 1] as const;

export function SceneContainer({
  id,
  ariaLabel,
  minHeight = "min-h-screen",
  className = "",
  children,
}: SceneContainerProps) {
  const prefersReducedMotion = useReducedMotion();

  const labelProps = ariaLabel
    ? { "aria-label": ariaLabel }
    : { "aria-labelledby": `${id}-title` };

  return (
    <motion.section
      id={id}
      role="region"
      {...labelProps}
      className={`${minHeight} scroll-mt-16 max-lg:pb-[var(--landing-mobile-nav-clearance,0px)] ${className}`}
      initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
      whileInView={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
      transition={{ duration: 0.4, ease: APPLE_EASE_OUT }}
    >
      {children}
    </motion.section>
  );
}
